import { styled } from '@mui/material/styles';
import { updateColorLight } from 'utils/colorHelpers';

/**
 * SPECS:
 * - base font size: 14px
 * - sizes (heights): 42px, 32px, 22px
 */
const LINE_HEIGHTS = {
  large: '1.875em',
  medium: '1.75em',
  small: '1.1875em'
};
const MIN_WIDTHS = {
  large: '150px',
  medium: '100px',
  small: '50px'
};
const PADDINGS = {
  large: [1, 3],
  medium: [0.5, 2],
  small: [0.375, 1.5]
};
const DISABLED_SELECTOR = '&:disabled, &[disabled], &[aria-disabled="true"], &[data-disabled="true"]';

export const CustomContent = styled('div')(() => ({
  display: 'flex'
}));

export const Label = styled('span')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

export const Button = styled('button', {
  shouldForwardProp: prop => !['$fullWidth', '$size', '$color', '$variant'].includes(prop)
})(({ theme, $fullWidth, $size, $color, $variant }) => {
  const size = $size ?? 'medium';
  const lineHeight = LINE_HEIGHTS[size] || LINE_HEIGHTS.medium;
  const minWidth = MIN_WIDTHS[size] || MIN_WIDTHS.medium;
  const paddings = PADDINGS[size] || PADDINGS.medium;
  const color = theme.color[$color];
  const disabledCursorStyles = {
    cursor: 'default !important',
    pointerEvents: 'none'
  };

  const getVariantStyles = () => {
    if ($variant === 'contained') {
      return {
        '&, &:visited': {
          background: `linear-gradient(270deg, ${color.main} 0%, ${color.light} 100%)`,
          color: color.contrast
        },
        '&:hover': {
          background: `linear-gradient(270deg, ${color.light} 0%, ${color.main} 100%)`
        },
        '&:active': {
          background: `linear-gradient(270deg, ${color.dark} 0%, ${color.main} 100%)`
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: theme.color.disabled.background,
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: theme.color.disabled.background,
            color: theme.color.disabled.text
          }
        }
      };
    }

    if ($variant === 'outlined') {
      return {
        '&, &:visited': {
          boxShadow: `inset 0px 0px 0px 1px ${color.main}`,
          color: color.main
        },
        '&:hover': {
          background: updateColorLight(color.main, 95)
        },
        '&:active': {
          background: updateColorLight(color.main, 85)
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: theme.color.disabled.background,
          boxShadow: `inset 0px 0px 0px 1px ${theme.color.disabled.border}`,
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: theme.color.disabled.background,
            boxShadow: `inset 0px 0px 0px 1px ${theme.color.disabled.border}`,
            color: theme.color.disabled.text
          }
        }
      };
    }

    if ($variant === 'text') {
      return {
        '&, &:visited': {
          color: color.main
        },
        '&:hover': {
          background: updateColorLight(color.main, 95)
        },
        '&:active': {
          background: updateColorLight(color.main, 85)
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: theme.color.disabled.background,
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: theme.color.disabled.background,
            color: theme.color.disabled.text
          }
        }
      };
    }

    if ($variant === 'lightContained') {
      return {
        '&, &:visited': {
          background: color.contrast,
          color: color.main
        },
        '&:hover': {
          background: updateColorLight(color.main, 95)
        },
        '&:active': {
          background: updateColorLight(color.main, 85)
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: theme.color.disabled.background,
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: theme.color.disabled.background,
            color: theme.color.disabled.text
          }
        }
      };
    }

    if ($variant === 'lightOutlined') {
      return {
        '&, &:visited': {
          boxShadow: `inset 0px 0px 0px 1px ${color.contrast}`,
          color: color.contrast
        },
        '&:hover': {
          background: color.contrast,
          color: color.main
        },
        '&:active': {
          background: updateColorLight(color.main, 95),
          color: color.main
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: 'none',
          boxShadow: `inset 0px 0px 0px 1px ${theme.color.disabled.border}`,
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: 'none',
            boxShadow: `inset 0px 0px 0px 1px ${theme.color.disabled.border}`,
            color: theme.color.disabled.text
          }
        }
      };
    }

    if ($variant === 'lightText') {
      return {
        '&, &:visited': {
          color: color.contrast
        },
        '&:hover': {
          background: color.contrast,
          color: color.main
        },
        '&:active': {
          background: updateColorLight(color.main, 95),
          color: color.main
        },
        [DISABLED_SELECTOR]: {
          ...disabledCursorStyles,
          background: 'none',
          color: theme.color.disabled.text,
          '&:hover, &:active': {
            background: 'none',
            color: theme.color.disabled.text
          }
        }
      };
    }

    return {};
  };

  return {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    borderRadius: theme.borderRadius.base,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.small,
    justifyContent: 'center',
    overflow: 'hidden',
    textTransform: 'uppercase',
    transition: `all ${theme.animation.standard}`,
    ...($fullWidth && { width: '100%' }),
    lineHeight: lineHeight,
    minWidth: minWidth,
    padding: theme.spacing(...paddings),
    ...getVariantStyles(),
    '&&': {
      WebkitAppearance: 'none'
    },
    [`& * + ${Label}, & ${Label} + *`]: {
      marginLeft: theme.spacing(0.5)
    }
  };
});
